import React, { useState, useMemo, useEffect, useCallback, useRef, createRef } from "react"
import { Text, VStack } from "@chakra-ui/react"
import StatusToggleWorkflow, { WorkflowState, WorkflowMessages } from "@hydrologiq/shared/chakra/StatusToggleWorkflow"
import VolumePressureInputs, { VolumePressureInputsRef } from "@hydrologiq/shared/figures/asset/VolumePressureInputs"
import { isValidVolumeConfig } from "./activationUtils"
import type { DynamicStorageAssetConfig, AssetVolumeConfig } from "./activationUtils"

export interface ConnectorActivationWorkflowProps {
  open: boolean
  action: "activate" | "deactivate" | null
  /** Assets requiring configuration (dynamic_storage assets in scope) */
  dynamicStorageAssets: DynamicStorageAssetConfig[]
  /** Callback to execute activation with collected values */
  onActivate: (assetConfigs: AssetVolumeConfig[]) => Promise<void>
  onDeactivate: () => Promise<void>
  onClose: () => void
  onSuccess: () => void
  onError: (error: Error) => void
  container?: React.RefObject<HTMLElement | null>
}

/**
 * ConnectorActivationWorkflow intercepts the data connection activation flow
 * when dynamic_storage assets are present. It collects volume and minimum pressure
 * values via a dialog before proceeding with activation.
 *
 * Behavior:
 * - activate + dynamicStorageAssets: show confirming dialog with VolumePressureInputs
 * - activate + no dynamicStorageAssets: passthrough (call onActivate immediately)
 * - deactivate: passthrough (call onDeactivate immediately)
 */
const ConnectorActivationWorkflow = ({
  open,
  action,
  dynamicStorageAssets,
  onActivate,
  onDeactivate,
  onClose,
  onSuccess,
  onError,
  container,
}: ConnectorActivationWorkflowProps) => {
  // Internal workflow state
  const [workflowState, setWorkflowState] = useState<WorkflowState>("idle")
  const [pendingAction, setPendingAction] = useState<"activate" | "deactivate" | null>(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  // Refs for each VolumePressureInputs instance
  const inputRefsRef = useRef<React.RefObject<VolumePressureInputsRef | null>[]>([])

  // Keep refs array in sync with dynamicStorageAssets length
  useMemo(() => {
    inputRefsRef.current = dynamicStorageAssets.map(
      (_, i) => inputRefsRef.current[i] ?? createRef<VolumePressureInputsRef>()
    )
  }, [dynamicStorageAssets.length])

  // Validation: check all inputs have valid numeric values
  // volume > 0 and minPressure >= 0
  const validateAllInputs = useCallback((): boolean => {
    return inputRefsRef.current.every((ref) => {
      const values = ref.current?.getValues()
      if (!values) return false
      return isValidVolumeConfig(values.volume, values.minPressure)
    })
  }, [])

  // Collect values from all input refs
  const collectAssetConfigs = useCallback((): AssetVolumeConfig[] => {
    return dynamicStorageAssets.map((asset, i) => {
      const values = inputRefsRef.current[i]?.current?.getValues()
      return {
        assetId: asset.assetId,
        volume: values?.volume ? parseFloat(values.volume) : null,
        minPressure: values?.minPressure ? parseFloat(values.minPressure) : null,
      }
    })
  }, [dynamicStorageAssets])

  // Handle confirm — validates before proceeding
  const handleConfirm = useCallback(async () => {
    if (!validateAllInputs()) {
      setErrorMessage("Please enter valid volume (> 0) and minimum pressure (\u2265 0) for all assets.")
      return
    }

    setErrorMessage(null)
    setWorkflowState("processing")

    try {
      const configs = collectAssetConfigs()
      await onActivate(configs)

      setWorkflowState("success")
      onSuccess()
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : "Failed to activate connection"
      setErrorMessage(errorMsg)
      setWorkflowState("error")
      onError(error instanceof Error ? error : new Error(errorMsg))
    }
  }, [validateAllInputs, collectAssetConfigs, onActivate, onSuccess, onError])

  // Handle cancel
  const handleCancel = useCallback(() => {
    if (workflowState === "processing") return

    setWorkflowState("idle")
    setPendingAction(null)
    setErrorMessage(null)
    onClose()
  }, [workflowState, onClose])

  // Handle dialog close (from success/error state)
  const handleCloseDialog = useCallback(() => {
    if (workflowState === "processing") return

    if (workflowState === "success") {
      setWorkflowState("idle")
      setPendingAction(null)
      setErrorMessage(null)
      onClose()
      return
    }

    handleCancel()
  }, [workflowState, handleCancel, onClose])

  // Sync with parent open/action props
  useEffect(() => {
    if (open && action && workflowState === "idle") {
      setPendingAction(action)

      if (action === "deactivate") {
        // Deactivation: passthrough — no dialog
        onDeactivate().catch((error) => {
          const errorMsg = error instanceof Error ? error.message : "Failed to deactivate connection"
          onError(error instanceof Error ? error : new Error(errorMsg))
        })
        onClose()
        return
      }

      // Activation
      if (dynamicStorageAssets.length === 0) {
        // Passthrough: no dynamic_storage assets, activate immediately
        onActivate([]).catch((error) => {
          const errorMsg = error instanceof Error ? error.message : "Failed to activate connection"
          onError(error instanceof Error ? error : new Error(errorMsg))
        })
        onClose()
        return
      }

      // Has dynamic_storage assets: show confirming dialog
      setWorkflowState("confirming")
    }
  }, [open, action])

  // Reset state when dialog closes
  useEffect(() => {
    if (!open && workflowState !== "idle") {
      setWorkflowState("idle")
      setPendingAction(null)
      setErrorMessage(null)
      // Reset all input refs
      inputRefsRef.current.forEach((ref) => ref.current?.reset())
    }
  }, [open])

  // Generate workflow messages
  const workflowMessages = useMemo((): WorkflowMessages | null => {
    if (!pendingAction) return null
    return {
      processing: "Please wait while the connection is being activated.",
      success: "Connection activated. It may take up to 5 minutes for data to appear.",
      error:
        errorMessage ||
        "The activation request has failed. Please contact the Hydrologiq support team or try again.",
    }
  }, [pendingAction, errorMessage])

  // Build confirmation content with VolumePressureInputs for each asset
  const confirmationContent = useMemo(() => {
    if (workflowState !== "confirming" || pendingAction !== "activate") return null

    const showLabels = dynamicStorageAssets.length > 1

    return (
      <VStack align="stretch" gap={4}>
        <Text>
          Configure volume and minimum pressure for{" "}
          {dynamicStorageAssets.length === 1
            ? "the dynamic storage asset"
            : `${dynamicStorageAssets.length} dynamic storage assets`}{" "}
          before activating.
        </Text>
        {dynamicStorageAssets.map((asset, index) => (
          <VStack key={asset.assetId} align="stretch" gap={2}>
            {showLabels && (
              <Text fontWeight="medium" fontSize="sm">
                {asset.assetName || `Asset ${index + 1}`}
              </Text>
            )}
            <VolumePressureInputs
              ref={inputRefsRef.current[index]}
              required
              defaultVolume={asset.defaultVolume}
              defaultMinPressure={asset.defaultMinPressure}
              error={errorMessage && workflowState === "confirming" ? errorMessage : undefined}
            />
          </VStack>
        ))}
      </VStack>
    )
  }, [workflowState, pendingAction, dynamicStorageAssets, errorMessage])

  // Custom titles
  const customTitles = useMemo(
    () => ({
      confirming: "Configure activation",
      processing: "Activating",
      success: "Connection activated",
      error: "Activation failed",
    }),
    []
  )

  // Don't render when idle and not open
  if (!open && workflowState === "idle") {
    return null
  }

  // Don't render dialog for passthrough modes
  if (workflowState === "idle") {
    return null
  }

  return (
    <StatusToggleWorkflow
      workflowState={workflowState}
      pendingAction={pendingAction}
      messages={workflowMessages}
      onConfirm={handleConfirm}
      onCancel={workflowState === "processing" ? undefined : handleCancel}
      onClose={handleCloseDialog}
      container={container}
      confirmationContent={confirmationContent}
      customTitles={customTitles}
      showSkipButton={false}
    />
  )
}

const MemoizedConnectorActivationWorkflow = React.memo(ConnectorActivationWorkflow)
export default MemoizedConnectorActivationWorkflow
